import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Dialog } from '@angular/cdk/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AccordionComponent,
  AccordionItemComponent,
  ConfirmDialogComponent,
  Dropdown,
  DropdownOption,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  LoadingStateComponent,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  SelectionRowComponent,
  type ProfileDangerAction,
  type ProfileQuickAction,
  ToastService,
  ZagButton,
  ZagIcon,
  hasAuditLogAccess,
  SidebarNavStateService,
  wireAuditPanelAutoOpen,
} from '@zag/ui';
import { AuditLogPanelComponent } from '@zag/core/ui';
import { UsersManagementService, type UserGroup, type UserPermission } from '../users-management.service';
import { EntityAccessService } from '../entity-access.service';
import type { RecipientAccessMode } from '@zag/auth/models';
import { RolesManagementService } from '../roles-management.service';
import { UserEntityAccessPanelComponent } from '../user-entity-access-panel/user-entity-access-panel.component';
import { forkJoin, Observable, of } from 'rxjs';
import { AuthService } from '../auth.service';
import { PermissionService } from '../permission.service';

@Component({
  selector: 'sc-user-edit',
  imports: [
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    SelectionRowComponent,
    AccordionComponent,
    AccordionItemComponent,
    Dropdown,
    ZagButton,
    ZagIcon,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
    AuditLogPanelComponent,
    UserEntityAccessPanelComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.scss',
})
export class UserEditComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });
  private readonly dialog = inject(Dialog);
  private readonly usersService = inject(UsersManagementService);
  private readonly accessService = inject(EntityAccessService);
  private readonly rolesService = inject(RolesManagementService);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly sidebarNavState = inject(SidebarNavStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Whether the audit log panel is open */
  readonly auditPanelOpen = signal(false);
  readonly canAccessAuditLog = computed(() => hasAuditLogAccess(this.permissionService));

  /** Whether the current admin may trigger a password-reset email for this user. */
  readonly canSendPasswordReset = computed(() => this.permissionService.hasPermission('rbac:users:update'));

  /** Whether the viewed user currently has must-change-password set */
  readonly mustChangePassword = computed(() => this.selectedUser()?.mustChangePassword ?? false);

  /** Route params as signal for reactive updates */
  private readonly routeParams = toSignal(this.route.paramMap);

  // User ID from route - reactive
  readonly userId = computed(() => this.routeParams()?.get('id') ?? null);

  // Use the existing service's selected user
  private readonly selectedUser = this.usersService.selectedUser;

  // Loading state
  readonly isLoading = computed(() => {
    const id = this.userId();
    if (!id) return false;
    return this.usersService.isLoadingSelected();
  });

  // Error state
  readonly error = computed(() => {
    this.currentLang();
    const id = this.userId();
    if (!id) return null;
    const userError = this.usersService.error();
    if (userError) {
      return this.translate.instant('settings.users.loadError');
    }
    return null;
  });

  // Track save state for all operations
  protected readonly isSaving = signal(false);

  // Roles hash captured at save time; null = no refresh pending
  private readonly _pendingRolesHash = signal<string | null>(null);

  // Stable snapshot of the loaded user's role IDs for change detection
  private readonly _currentRolesHash = computed(() => {
    const user = this.selectedUser();
    if (!user) return '';
    return (user.directRoles ?? [])
      .map(r => r.id)
      .sort((a, b) => a.localeCompare(b))
      .join(',');
  });

  // ============================================================================
  // Profile Card Data
  // ============================================================================

  /** Current user name for profile card */
  readonly userName = computed(() => {
    this.currentLang();
    const user = this.selectedUser();
    return user?.name ?? this.translate.instant('settings.users.loading');
  });

  /** User email for subtitle */
  readonly userEmail = computed(() => {
    const user = this.selectedUser();
    return user?.email ?? '';
  });

  /** User avatar image */
  readonly userImage = computed(() => {
    const user = this.selectedUser();
    return user?.image ?? undefined;
  });

  /** Profile subtitle */
  readonly userSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.users.entityType');
  });

  /** True when the profile being viewed belongs to the currently logged-in user */
  private readonly isSelf = computed(() => this.userId() !== null && this.userId() === this.authService.currentUser()?.id);

  /** Danger actions — delete requires rbac:users:update and is hidden for own profile */
  readonly dangerActions = computed<ProfileDangerAction[]>(() => {
    this.currentLang();
    if (this.isSelf() || !this.canSendPasswordReset()) return [];
    return [
      {
        id: 'delete',
        label: this.translate.instant('settings.users.deleteUser'),
        icon: 'delete',
        disabled: false,
      },
    ];
  });

  /** Quick actions for profile card */
  readonly quickActions = computed<ProfileQuickAction[]>(() => {
    this.currentLang();
    const actions: ProfileQuickAction[] = [];

    if (this.canAccessAuditLog()) {
      actions.push({
        id: 'audit-log',
        label: this.translate.instant('common.activityLog'),
        icon: 'history',
        disabled: false,
      });
    }

    // Admins can email the user a password-reset link.
    if (this.canSendPasswordReset()) {
      actions.push({
        id: 'send-password-reset',
        label: this.translate.instant('settings.users.actions.sendPasswordReset'),
        icon: 'lock_reset',
        disabled: false,
      });

      // Toggle must-change-password flag (active state = flag is set → action clears it)
      const flagActive = this.mustChangePassword();
      actions.push({
        id: 'toggle-must-change-password',
        label: flagActive
          ? this.translate.instant('settings.users.actions.clearMustChangePassword')
          : this.translate.instant('settings.users.actions.forceMustChangePassword'),
        icon: flagActive ? 'lock_open' : 'lock',
        disabled: this.isSelf(),
      });
    }

    return actions;
  });

  // ============================================================================
  // Person Linking (Simplified - single dropdown with clearable)
  // ============================================================================

  readonly linkedPerson = this.usersService.linkedPerson;
  readonly unlinkedPersons = this.usersService.unlinkedPersons;
  readonly unlinkedPersonsLoading = this.usersService.unlinkedPersonsLoading;

  /**
   * All available persons for the dropdown (linked + unlinked).
   * The currently linked person is included so it shows as selected.
   */
  readonly allPersonOptions = computed<DropdownOption<string>[]>(() => {
    const linked = this.linkedPerson();
    const unlinked = this.unlinkedPersons();

    const options: DropdownOption<string>[] = [];

    // Add currently linked person first (if exists)
    if (linked) {
      options.push({
        label: `${linked.firstName} ${linked.lastName}`,
        value: linked.id,
      });
    }

    // Add all unlinked persons
    for (const person of unlinked) {
      options.push({
        label: `${person.firstName} ${person.lastName}`,
        value: person.id,
      });
    }

    return options;
  });

  /**
   * Track person change locally for combined save.
   * null = no change staged
   * { personId: string } = link or reassign to this person
   * { personId: null } = unlink (clear)
   */
  private readonly _stagedPersonId = signal<{ personId: string | null } | null>(null);

  /** The person ID to display in the dropdown (staged or current) */
  readonly displayedPersonId = computed<string | null>(() => {
    const staged = this._stagedPersonId();
    if (staged !== null) {
      return staged.personId;
    }
    return this.linkedPerson()?.id ?? null;
  });

  /** Check if there's an unsaved person change */
  readonly hasUnsavedPersonChange = computed(() => {
    const staged = this._stagedPersonId();
    if (staged === null) return false;

    const currentLinked = this.linkedPerson()?.id ?? null;
    return staged.personId !== currentLinked;
  });

  /** Handle person selection from dropdown (including clear via null) */
  protected onPersonSelectionChange(personId: string | null): void {
    const currentLinked = this.linkedPerson()?.id ?? null;

    // If selecting the same person that's already linked, reset staging
    if (personId === currentLinked) {
      this._stagedPersonId.set(null);
      return;
    }

    // Stage the change
    this._stagedPersonId.set({ personId });

    // Show appropriate toast
    if (personId === null) {
      this.toast.info(this.translate.instant('settings.users.personUnlinkStaged'));
    } else if (currentLinked) {
      const linked = this.linkedPerson();
      const fromName = linked ? `${linked.firstName} ${linked.lastName}` : '';
      this.toast.info(
        this.translate.instant('settings.users.personReassignStaged', {
          from: fromName,
        })
      );
    } else {
      this.toast.info(this.translate.instant('settings.users.personLinkStaged'));
    }
  }

  // ============================================================================
  // Direct Roles Management
  // ============================================================================

  /** All available roles from roles service */
  readonly availableRoles = this.rolesService.roles;
  readonly rolesLoading = computed(() => this.rolesService.isLoading());

  /** Current user's direct role IDs */
  readonly directRoleIds = computed(() => {
    const user = this.selectedUser();
    return new Set(user?.directRoles?.map(r => r.id) ?? []);
  });

  /** Local state for role checkboxes (before save) */
  private readonly _localRoleIds = signal<Set<string> | null>(null);

  /** Computed role IDs to display (local changes or backend data) */
  readonly displayedRoleIds = computed(() => {
    const local = this._localRoleIds();
    if (local !== null) return local;
    return this.directRoleIds();
  });

  /** Check if there are unsaved role changes */
  readonly hasUnsavedRoleChanges = computed(() => {
    const local = this._localRoleIds();
    if (local === null) return false;

    const backend = this.directRoleIds();
    if (local.size !== backend.size) return true;

    for (const id of local) {
      if (!backend.has(id)) return true;
    }
    return false;
  });

  /** Combined check for any pending changes */
  readonly hasPendingChanges = computed(() => {
    return this.hasUnsavedRoleChanges() || this.hasUnsavedGroupChanges() || this.hasUnsavedPersonChange();
  });

  // ============================================================================
  // Effective Permissions (Read-only)
  // ============================================================================

  readonly effectivePermissions = computed<UserPermission[]>(() => {
    const user = this.selectedUser();
    return user?.effectivePermissions ?? [];
  });

  readonly inheritedRoles = computed(() => {
    const user = this.selectedUser();
    return user?.inheritedRoles ?? [];
  });

  private readonly recipientAccessResource = rxResource({
    params: () => ({ userId: this.userId() }),
    stream: ({ params }) => (params.userId ? this.accessService.getRecipientAccess(params.userId) : of(null)),
    defaultValue: null as RecipientAccessMode | null,
  });

  /** Entity grants only exist for scoped recipients — hide the panel otherwise. */
  readonly showEntityAccessPanel = computed(() => this.recipientAccessResource.value()?.eligibleForDirectGrant === true);

  // ============================================================================
  // Group Management State (from service)
  // ============================================================================

  /** All groups (backend + local unsaved additions, excluding local removals) */
  protected readonly allGroups = this.usersService.allGroups;

  /** Groups loading state */
  protected readonly groupsLoading = this.usersService.groupsLoading;

  /** Check if there are unsaved group changes */
  protected readonly hasUnsavedGroupChanges = this.usersService.hasUnsavedGroupChanges;

  /** Unassigned groups available for adding */
  protected readonly unassignedGroups = this.usersService.availableUnassignedGroups;

  /** Dropdown options for unassigned groups */
  protected readonly unassignedGroupOptions = computed<DropdownOption<string>[]>(() => {
    return this.unassignedGroups().map(group => ({
      label: group.description ? `${group.name} (${group.description})` : group.name,
      value: group.id,
    }));
  });

  /** Loading state for unassigned groups */
  protected readonly unassignedGroupsLoading = this.usersService.unassignedGroupsLoading;

  /** Signal for selected group in dropdown (for adding) */
  protected readonly selectedGroupToAdd = signal<string | null>(null);

  /** Track previous ID to detect changes */
  private previousUserId: string | null = null;

  constructor() {
    wireAuditPanelAutoOpen({
      isNew: computed(() => !this.userId()),
      canAccess: this.canAccessAuditLog,
      isSidebarCollapsed: this.sidebarNavState.isCollapsed,
      auditPanelOpen: this.auditPanelOpen,
      breakpointObserver: this.breakpointObserver,
      destroyRef: this.destroyRef,
      injector: this.injector,
    });

    // Load roles list for checkboxes
    this.rolesService.reload();

    // React to route param changes: reset state and load user data.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(() => {
      const currentId = this.userId();
      if (currentId !== this.previousUserId) {
        this.previousUserId = currentId;
        this.selectedGroupToAdd.set(null);
        this._stagedPersonId.set(null);
        this._localRoleIds.set(null);

        if (currentId) {
          this.usersService.selectById(currentId);
          this.usersService.reloadPersons();
          this.usersService.reloadGroups();
        }
      }
    });

    // Clear local state when fresh data arrives after save.
    effect(() => {
      const pending = this._pendingRolesHash();
      if (pending === null) return;
      if (this._currentRolesHash() !== pending) {
        this._localRoleIds.set(null);
        this._stagedPersonId.set(null);
        this._pendingRolesHash.set(null);
      }
    });
  }

  // ============================================================================
  // Role Management Methods
  // ============================================================================

  protected setRoleSelection(roleId: string, checked: boolean): void {
    // Initialize local state from backend if not already
    if (this._localRoleIds() === null) {
      this._localRoleIds.set(new Set(this.directRoleIds()));
    }

    const current = this._localRoleIds();
    if (current === null) return;
    const newSet = new Set(current);

    if (checked) newSet.add(roleId);
    else newSet.delete(roleId);

    this._localRoleIds.set(newSet);
  }

  protected isRoleSelected(roleId: string): boolean {
    return this.displayedRoleIds().has(roleId);
  }

  /** Check if a specific role was changed from its original state (for visual highlight) */
  protected isRoleChanged(roleId: string): boolean {
    const local = this._localRoleIds();
    if (local === null) return false;

    const backend = this.directRoleIds();
    const wasSelected = backend.has(roleId);
    const isNowSelected = local.has(roleId);

    return wasSelected !== isNowSelected;
  }

  // ============================================================================
  // Group Management Methods
  // ============================================================================

  protected onGroupToAddSelected(groupId: string | null): void {
    if (!groupId) return;

    const group = this.unassignedGroups().find(g => g.id === groupId);
    if (group) {
      this.usersService.addGroupLocally(group, false);
      this.toast.success(this.translate.instant('settings.users.groupAddedLocal'));
    }

    this.selectedGroupToAdd.set(null);
  }

  protected removeGroup(group: UserGroup): void {
    this.usersService.removeGroupLocally(group.id);
  }

  protected isLocalGroupAddition(group: UserGroup): boolean {
    const localAdded = this.usersService.localAddedGroups();
    return localAdded.some(g => g.groupId === group.id);
  }

  // ============================================================================
  // Combined Save/Discard for All Changes
  // ============================================================================

  protected saveAll(): void {
    const userId = this.userId();
    if (!userId || !this.hasPendingChanges()) return;

    this.isSaving.set(true);
    const operations: Observable<unknown>[] = [];

    // Add role update if changed
    if (this.hasUnsavedRoleChanges() && this._localRoleIds()) {
      operations.push(this.usersService.updateUserRoles(userId, Array.from(this._localRoleIds() ?? [])));
    }

    // Add group update if changed
    if (this.hasUnsavedGroupChanges()) {
      operations.push(this.usersService.saveGroupChanges(userId));
    }

    // Add person link/unlink/reassign if staged
    if (this.hasUnsavedPersonChange()) {
      const stagedPersonId = this._stagedPersonId()?.personId;
      const currentLinkedId = this.linkedPerson()?.id;

      if (stagedPersonId === null && currentLinkedId) {
        // Unlink: clear to null when there was a linked person
        operations.push(this.usersService.unlinkPerson(userId));
      } else if (stagedPersonId && !currentLinkedId) {
        // Link: new person when none was linked
        operations.push(this.usersService.linkPerson(userId, stagedPersonId));
      } else if (stagedPersonId && currentLinkedId) {
        // Reassign: different person when one was already linked
        operations.push(this.usersService.reassignPerson(userId, stagedPersonId));
      }
    }

    forkJoin(operations.length > 0 ? operations : [of(null)]).subscribe({
      next: () => {
        this.isSaving.set(false);
        // Clear group changes immediately (service handles its own state)
        this.usersService.clearLocalGroupChanges();
        this.toast.success(this.translate.instant('settings.users.saveSuccess'));
        // Record hash now — effect clears local state when fresh data arrives
        this._pendingRolesHash.set(this._currentRolesHash());
        this.usersService.reloadSelected();
      },
      error: (err: { error?: { message?: string } }) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || this.translate.instant('settings.users.saveError'));
      },
    });
  }

  // ============================================================================
  // Navigation
  // ============================================================================

  protected async onCancel(): Promise<void> {
    await this.router.navigate(['/settings/users']);
  }

  /** Handle quick action click */
  protected onQuickAction(action: ProfileQuickAction): void {
    if (action.id === 'audit-log') {
      this.auditPanelOpen.set(!this.auditPanelOpen());
    } else if (action.id === 'send-password-reset') {
      this.onSendPasswordReset();
    } else if (action.id === 'toggle-must-change-password') {
      this.onToggleMustChangePassword();
    }
  }

  private onSendPasswordReset(): void {
    const userId = this.userId();
    const user = this.selectedUser();
    if (!userId || !user) return;

    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
      width: 'min(96vw, 800px)',
      maxWidth: '96vw',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        title: this.translate.instant('settings.users.passwordReset.confirm.title'),
        message: this.translate.instant('settings.users.passwordReset.confirm.message', {
          name: user.name || user.email,
        }),
        confirmText: this.translate.instant('settings.users.actions.sendPasswordReset'),
      },
    });

    dialogRef.closed.subscribe(confirmed => {
      if (!confirmed) return;

      this.usersService.sendPasswordReset(userId).subscribe({
        next: () => {
          this.toast.success(this.translate.instant('settings.users.passwordReset.success'));
        },
        error: (err: { error?: { message?: string } }) => {
          this.toast.error(err?.error?.message || this.translate.instant('settings.users.passwordReset.error'));
        },
      });
    });
  }

  private onToggleMustChangePassword(): void {
    const userId = this.userId();
    const user = this.selectedUser();
    if (!userId || !user) return;

    const forcing = !this.mustChangePassword();
    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
      width: 'min(96vw, 480px)',
      maxWidth: '96vw',
      data: {
        title: forcing
          ? this.translate.instant('settings.users.mustChangePassword.forceTitle')
          : this.translate.instant('settings.users.mustChangePassword.clearTitle'),
        message: forcing
          ? this.translate.instant('settings.users.mustChangePassword.forceMessage', { name: user.name || user.email })
          : this.translate.instant('settings.users.mustChangePassword.clearMessage', { name: user.name || user.email }),
        confirmText: forcing
          ? this.translate.instant('settings.users.actions.forceMustChangePassword')
          : this.translate.instant('settings.users.actions.clearMustChangePassword'),
        destructive: forcing,
      },
    });

    dialogRef.closed.subscribe(confirmed => {
      if (!confirmed) return;
      this.usersService.setMustChangePassword(userId, forcing).subscribe({
        next: () => {
          this.toast.success(
            forcing
              ? this.translate.instant('settings.users.mustChangePassword.forceSuccess')
              : this.translate.instant('settings.users.mustChangePassword.clearSuccess')
          );
        },
        error: (err: { error?: { message?: string } }) => {
          this.toast.error(err?.error?.message || this.translate.instant('settings.users.mustChangePassword.error'));
        },
      });
    });
  }

  protected onDangerAction(action: ProfileDangerAction): void {
    if (action.id === 'delete') {
      this.onDelete();
    }
  }

  private onDelete(): void {
    const userId = this.userId();
    const user = this.selectedUser();
    if (!userId || !user) return;

    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
      width: 'min(96vw, 800px)',
      maxWidth: '96vw',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        title: this.translate.instant('settings.users.deleteUser'),
        message: this.translate.instant('settings.users.deleteUserConfirm', {
          name: user.name || user.email,
        }),
        confirmText: this.translate.instant('common.delete'),
        destructive: true,
      },
    });

    dialogRef.closed.subscribe(confirmed => {
      if (!confirmed) return;

      this.usersService.deleteUser(userId).subscribe({
        next: () => {
          this.toast.success(this.translate.instant('settings.users.deleteUserSuccess'));
          this.router.navigate(['/settings/users']);
        },
        error: (err: { error?: { message?: string } }) => {
          this.toast.error(err?.error?.message || this.translate.instant('settings.users.deleteUserError'));
        },
      });
    });
  }
}
